import { SharedModule } from '@/app/shared/shared.module';
import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  signal,
  computed,
  Output,
  EventEmitter,
} from '@angular/core';
import { HomePageMapViewModel, HomePageMapViewModelResponseResult, HomePageProxy, UserEnterpriseInfoViewModel, HomePageLandViewModel, HomePagePlantationViewModel } from '@/app/proxy/proxies';
import { HomeMapWidgetService, AssetCounts } from './home-map-widget.service';
import { filter, finalize, Subject, take, takeUntil } from 'rxjs';
import { DoktarHQCoordinates } from '@/app/shared/consts/mapbox-consts';
import { PlantationFilterResponse, PlantationSelectPanelFilterModalComponent } from '../../services/plantation-select-panel/plantation-select-panel-filter-modal/plantation-select-panel-filter-modal.component';
import { DialogService } from 'primeng/dynamicdialog';
import { PlantationService } from '@/app/core/services/plantation.service';
import { DeviceCountIndicatorComponent, DeviceCountInfo } from './device-count-indicator/device-count-indicator.component';
import { UserService } from '@/app/core/services/user.service';
import { AreaUnit, AreaUnitUtils } from '@/app/shared/consts/area-unit.consts';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

@Component({
  selector: 'dk-map-widget',
  imports: [SharedModule, DeviceCountIndicatorComponent],
  templateUrl: './map-widget.component.html',
  styleUrl: './map-widget.component.less',
})
export class MapWidgetComponent implements OnInit, OnDestroy, OnChanges {
  @Input() selectedEnterprise: UserEnterpriseInfoViewModel | null = null;
  @Input() homeWidgetViewModel: HomePageMapViewModel | null = null;
  @Input() isFullScreen = false;

  @Output() fullScreen = new EventEmitter();

  private readonly destroy$ = new Subject<void>();
  private keyboardHandler?: (event: KeyboardEvent) => void;

  // Reactive state
  private readonly _assetCounts = signal<AssetCounts>({
    landCount: 0,
    plantationCount: 0,
    deviceCount: 0,
    totalArea: '',
  });
  private readonly _isMapLoaded = signal<boolean>(false);
  private readonly _mapError = signal<string | null>(null);
  private readonly _deviceCounts = signal<DeviceCountInfo[]>([]);

  // Public accessors
  readonly assetCounts = this._assetCounts.asReadonly();
  readonly isMapLoaded = this._isMapLoaded.asReadonly();
  readonly mapError = this._mapError.asReadonly();
  readonly deviceCounts = this._deviceCounts.asReadonly();

  // Map state
  showAddPlantationDialog = false;
  loading = false;
  
  // Filter state
  private originalHomeWidgetViewModel: HomePageMapViewModel | null = null;
  filterParameters: PlantationFilterResponse | null = null;
  hasActiveFilters = false;

  readonly mapState = computed<'no-plantation' | 'single-plantation' | 'multiple-plantations'>(() => {
    const counts = this.assetCounts();
    if (counts.plantationCount === 0) return 'no-plantation';
    if (counts.plantationCount === 1) return 'single-plantation';
    return 'multiple-plantations';
  });

  constructor(
    private readonly homeMapService: HomeMapWidgetService,
    private _dialogService: DialogService,
    private _plantationService: PlantationService,
    private _homeProxy: HomePageProxy,
    private _userService: UserService,
    private _translateService: TranslateService,
    private readonly _router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['selectedEnterprise']) {
      this.resetMapState();
      
      if (this.selectedEnterprise) {
        if (this.isMapLoaded()) {
          this.getMapWidgetCoordinates();
        } else {
          this.homeMapService.mapLoaded$.pipe(
            takeUntil(this.destroy$),
            filter(loaded => loaded),
            take(1)
          ).subscribe(() => this.getMapWidgetCoordinates());
        }
      }
    }

    if (changes?.['isFullScreen']) {
      this.homeMapService.setHomeMapAnalyticsContext({
        fullScreen: this.isFullScreen,
      });
    }
  }

  ngOnInit(): void {
    this.subscribeToAssetCounts();
    this.subscribeToMapLoaded();
    this.initializeMap();
    this.setupKeyboardShortcuts();

    this.homeMapService.setHomeMapAnalyticsContext({
      fullScreen: this.isFullScreen,
      filterBy: this.buildHomeMapFilterByList(this.filterParameters),
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
    }
  }

  private resetMapState(): void {
    this.homeWidgetViewModel = null;
    this.originalHomeWidgetViewModel = null;
    this.filterParameters = null;
    this.hasActiveFilters = false;
    this.homeMapService.setHomeMapAnalyticsContext({ filterBy: [] });
    this.homeMapService.clearMapLayers();
    this.showAddPlantationDialog = true;
    this._assetCounts.set({ landCount: 0, plantationCount: 0, deviceCount: 0, totalArea: '0' });
    this._deviceCounts.set([]);
  }

  getMapWidgetCoordinates(): void {
    this.loading = true;
    this._homeProxy
      .homePage_GetMapsByEnterprise(this.selectedEnterprise?.enterpriseId)
      .pipe(finalize(() => this.loading = false))
      .subscribe((res: HomePageMapViewModelResponseResult) => {
        if (res.isSuccess) {
          this.homeWidgetViewModel = res.result;
          this.originalHomeWidgetViewModel = res.result;
          this.calculateDeviceCounts();
          this.loadMapData();
        } else {
          this.updatePlantationDialogState();
        }
      });
  }

  private subscribeToAssetCounts(): void {
    this.homeMapService.assetCounts$.pipe(takeUntil(this.destroy$)).subscribe((counts) => {
      this._assetCounts.set(counts);
      this.updatePlantationDialogState();
    });
  }

  private subscribeToMapLoaded(): void {
    this.homeMapService.mapLoaded$.pipe(takeUntil(this.destroy$)).subscribe((loaded) => {
      this._isMapLoaded.set(loaded);
    });
  }

  private initializeMap(): void {
    this.homeMapService
      .initializeMap('map-content', {
        center: DoktarHQCoordinates,
        zoom: 6,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (this.homeWidgetViewModel) {
            this.loadMapData();
          }
        },
        error: () => {
          this._mapError.set('Failed to load map. Please check your connection and try again.');
        },
      });
  }

  private loadMapData(): void {
    if (!this.homeWidgetViewModel || !this.isMapLoaded()) return;
    this.homeMapService.loadMapData(this.homeWidgetViewModel);
  }

  private updatePlantationDialogState(): void {
    this.showAddPlantationDialog = this.mapState() === 'no-plantation';
  }

  retryMapLoad(): void {
    this._mapError.set(null);
    this.initializeMap();
  }

  onAddPlantation(): void {
    const { landCount } = this.assetCounts();
    if (landCount < 1) {
      this._router.navigate(['/lands/add']);
    } else {
      this._router.navigate(['/lands']);
    }
  }

  onIgnorePlantation(): void {
    this.showAddPlantationDialog = false;
  }

  private setupKeyboardShortcuts(): void {
    this.keyboardHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && this.isFullScreen) {
        this.toggleFullScreenMode();
      }
    };
    document.addEventListener('keydown', this.keyboardHandler);
  }

  toggleFullScreenMode(): void {
    this.fullScreen.emit();
    setTimeout(() => this.homeMapService.map?.resize(), 300);
  }

  private calculateDeviceCounts(): void {
    if (!this.homeWidgetViewModel?.lands) {
      this._deviceCounts.set([]);
      return;
    }

    const deviceLines = this.homeMapService.deviceLinesCache;
    const deviceCountMap = new Map<string, DeviceCountInfo>();

    this.homeWidgetViewModel.lands.forEach(land => {
      land.plantations?.forEach(plantation => {
        plantation.devices?.forEach(device => {
          const deviceLine = deviceLines.find(dl => dl.code === device.deviceLineCode);
          if (deviceLine) {
            const existing = deviceCountMap.get(device.deviceLineCode);
            if (existing) {
              existing.count++;
            } else {
              deviceCountMap.set(device.deviceLineCode, {
                deviceLineCode: device.deviceLineCode,
                name: deviceLine.name || '',
                iconURL: deviceLine.iconURL || '',
                count: 1
              });
            }
          }
        });
      });
    });

    this._deviceCounts.set(Array.from(deviceCountMap.values()));
  }

  toggleFilterModal(): void {
    const modalRef = this._dialogService.open(PlantationSelectPanelFilterModalComponent, {
      header: this._translateService.instant('doktar_app.common.filter'),
      modal: true,
      closable: true,
      width: '450px',
      styleClass: 'PlantationSelectPanelFilterModalComponent',
      inputValues: {
        source: 'home',
        enterpriseOptions: this._plantationService.originalResult.filter(e => e.id === this.selectedEnterprise?.enterpriseId),
        filterParameters: this.filterParameters,
      },
    });

    // PrimeNG returns null when the same dialog is already open (e.g. a double click).
    if (!modalRef) {
      return;
    }

    modalRef.onClose.subscribe((res: PlantationFilterResponse) => {
      this.filterParameters = res;
      // Mixpanel için: Home Map Engaged — "filter by" property
      const filterBy = this.buildHomeMapFilterByList(res);
      this.homeMapService.setHomeMapAnalyticsContext({
        filterBy,
        fullScreen: this.isFullScreen,
      });
      if (res) {
        this.hasActiveFilters = true;
        this.applyFilters(res);
      } else {
        this.hasActiveFilters = false;
        this.clearFilters();
      }

      // Track each filter usage as a separate engagement event.
      this.homeMapService.trackHomeMapEngaged();
    });
  }

  /** Builds the "filter by" list sent with the Mixpanel Home Map Engaged event. Does not affect UI filtering. */
  private buildHomeMapFilterByList(filterResponse: PlantationFilterResponse | null): string[] {
    if (!filterResponse) return [];

    const filters: string[] = [];

    if (filterResponse.year) filters.push('Year');
    if (filterResponse.cropKeys?.length) filters.push('Crops');
    if (this.isAreaFilterActiveForAnalytics(filterResponse)) filters.push('Area');
    if (filterResponse.planKeys?.length) filters.push('Plans');
    if (filterResponse.deviceNames?.length) filters.push('Devices');

    return filters;
  }

  /** Tells Mixpanel whether "Area" is an active filter; true only when the slider is narrowed from the full 0–100% range. */
  private isAreaFilterActiveForAnalytics(filterResponse: PlantationFilterResponse): boolean {
    const range = filterResponse.totalAreaRange;
    if (!range || range.length !== 2 || range[1] === 0) {
      return false;
    }

    const maxArea = this.getMaxPlantationAreaFromOriginalData();
    if (maxArea <= 0) {
      return false;
    }

    const minPercent = (range[0] / maxArea) * 100;
    const maxPercent = (range[1] / maxArea) * 100;
    const isFullAreaRange = Math.abs(minPercent) < 0.01 && Math.abs(maxPercent - 100) < 0.01;

    return !isFullAreaRange;
  }

  /** Returns the largest plantation area in the original data; used by Mixpanel analytics to detect a full-range area slider. */
  private getMaxPlantationAreaFromOriginalData(): number {
    let maxArea = 0;
    this.originalHomeWidgetViewModel?.lands?.forEach((land) => {
      land.plantations?.forEach((plantation) => {
        const area = plantation.totalArea;
        if (area != null && area > maxArea) {
          maxArea = area;
        }
      });
    });
    return maxArea;
  }

  private matchesFilter(plantation: HomePagePlantationViewModel, f: PlantationFilterResponse): boolean {
    const yearMatch =
      !f.year ||
      !plantation.harvestEndDate ||
      plantation.harvestEndDate.year == f.year;

    const cropMatch =
      !(f.cropKeys?.length > 0) ||
      (plantation.crop && f.cropKeys.includes(plantation.crop.key));

    const areaMatch =
      !f.totalAreaRange ||
      f.totalAreaRange.length !== 2 ||
      f.totalAreaRange[1] == 0 ||
      (plantation.totalArea !== undefined &&
        plantation.totalArea >= f.totalAreaRange[0] &&
        plantation.totalArea <= f.totalAreaRange[1]);

    const planMatch =
      !(f.planKeys?.length > 0) ||
      f.planKeys.some(key => {
        if (key === 'premium') return plantation.isPremium;
        if (key === 'basic') return !plantation.isPremium;
        return true;
      });

    const deviceMatch =
      !(f.deviceNames?.length > 0) ||
      plantation.devices?.some((device: { name: string }) =>
        f.deviceNames!.some(name => name === device.name.replaceAll(',', ''))
      );

    return yearMatch && cropMatch && areaMatch && planMatch && deviceMatch;
  }

  private trimPlantation(plantation: HomePagePlantationViewModel, f: PlantationFilterResponse): ReturnType<HomePagePlantationViewModel['toJSON']> {
    const data = plantation.toJSON ? plantation.toJSON() : { ...plantation } as ReturnType<HomePagePlantationViewModel['toJSON']>;

    if (f.deviceNames?.length > 0) {
      data.devices = (data.devices ?? []).filter((device: { name: string }) =>
        f.deviceNames!.some(name => name === device.name.replaceAll(',', ''))
      );
    }

    return data;
  }

  private applyFilters(filterResponse: PlantationFilterResponse): void {
    if (!this.originalHomeWidgetViewModel?.lands) return;

    const filteredLands = this.originalHomeWidgetViewModel.lands
      .map(land => {
        const filteredPlantations = (land.plantations ?? [])
          .filter(p => this.matchesFilter(p, filterResponse))
          .map(p => this.trimPlantation(p, filterResponse));

        return filteredPlantations.length > 0
          ? HomePageLandViewModel.fromJS({
              id: land.id,
              name: land.name,
              boundaries: land.boundaries,
              plantations: filteredPlantations,
            })
          : null;
      })
      .filter((land): land is HomePageLandViewModel => land !== null);

    // Calculate total area from filtered plantations
    // Note: plantation.totalArea from the API is already in the user's preferred unit
    const filteredTotalArea = filteredLands.reduce((sum, land) => {
      return sum + (land.plantations?.reduce((pSum, p) => pSum + (p.totalArea || 0), 0) || 0);
    }, 0);

    // Get the unit abbreviation from user preferences
    const unitAbbreviation = this.getUserPreferredAreaUnitAbbreviation();
    const filteredTotalAreaDisplay = `${filteredTotalArea.toFixed(2)} ${unitAbbreviation}`;

    const filteredViewModel = HomePageMapViewModel.fromJS({
      plantationCount: filteredLands.reduce((sum, land) => sum + (land.plantations?.length || 0), 0),
      landCount: filteredLands.length,
      totalArea: filteredTotalArea,
      totalAreaDisplay: filteredTotalAreaDisplay,
      lands: filteredLands.map(l => l.toJSON ? l.toJSON() : l)
    });
    
    this.homeWidgetViewModel = filteredViewModel;
    this.calculateDeviceCounts();
    this.homeMapService.loadMapData(this.homeWidgetViewModel, true);
  }

  private clearFilters(): void {
    if (this.originalHomeWidgetViewModel) {
      this.homeWidgetViewModel = this.originalHomeWidgetViewModel;
      this.calculateDeviceCounts();
      this.homeMapService.loadMapData(this.homeWidgetViewModel, true);
    }
  }

  private getUserPreferredAreaUnitAbbreviation(): string {
    const unitPreferences = this._userService.getUnitPreferences();
    
    if (!unitPreferences || unitPreferences.length === 0) {
      return AreaUnitUtils.getAbbreviation(AreaUnit.Hectare);
    }

    const userUnitIds = unitPreferences.map((u: { key: string }) => u.key);
    const preferredUnit = AreaUnitUtils.isSomeUnitValidFromIds(userUnitIds);

    return preferredUnit 
      ? AreaUnitUtils.getAbbreviation(preferredUnit) 
      : AreaUnitUtils.getAbbreviation(AreaUnit.Hectare);
  }
}
